import { useState, useMemo } from 'react';
import { useSignalR } from '../hooks/useSignalR';
import { useGameRoom } from '../hooks/useGameRoom';
import { useGameStore } from '../stores/gameStore';
import { GameType, SpinnerArm } from '@shark-games/shared';
import { DominoHand } from '../components/game/DominoTile';
import { DominoBoard } from '../components/game/DominoBoard';
import { CardHand } from '../components/game/PlayingCard';
import { SpadesTable } from '../components/game/SpadesTable';

type GameMode = 'quick' | 'ai';

const games: { id: GameType; name: string; description: string; players: string; supportsAI: boolean }[] = [
  {
    id: 'spades',
    name: 'Spades',
    description: '4-player trick-taking game with bidding',
    players: '4 Players',
    supportsAI: true,
  },
  {
    id: 'bid_whist',
    name: 'Bid Whist',
    description: 'Classic partnership card game',
    players: '4 Players',
    supportsAI: true,
  },
  {
    id: 'tonk',
    name: 'Tonk',
    description: 'Fast-paced matching card game',
    players: '2-4 Players',
    supportsAI: true,
  },
  {
    id: 'dominoes_big6',
    name: 'Dominoes (Big 6)',
    description: 'Classic domino game',
    players: '2-4 Players',
    supportsAI: true,
  },
  {
    id: 'dominoes_big12',
    name: 'Dominoes (Big 12)',
    description: 'Extended domino game',
    players: '2-4 Players',
    supportsAI: true,
  },
];

export default function LobbyPage() {
  const [selectedGame, setSelectedGame] = useState<GameType | null>(null);
  const [gameMode, setGameMode] = useState<GameMode | null>(null);
  const [selectedDomino, setSelectedDomino] = useState<string | null>(null);
  const [draggingDomino, setDraggingDomino] = useState<string | null>(null);
  const [pendingGame, setPendingGame] = useState<{ gameType: GameType; mode: GameMode } | null>(null);
  const [scoreLimit, setScoreLimit] = useState(150);
  const { joinRoom, leaveRoom, invoke, roomId, isConnecting, error } = useSignalR();
  const {
    phase,
    players,
    mySessionId,
    currentTurnPlayerId,
    boneyardCount,
    // Spinner state
    hasSpinner,
    spinnerValue,
    spinnerId,
    leftArm,
    rightArm,
    topArm,
    bottomArm,
    openEnds,
    preSpinnerTiles,
    validPlays,
    // Spades state
    team1Score,
    team1Bags,
    team2Score,
    team2Bags,
    currentTrick,
    bids,
    tricksWon,
    roundNumber,
    spadesBroken,
    team1Bid,
    team2Bid,
    team1Tricks,
    team2Tricks,
    validCards,
    // Dominoes round/scoring
    roundResult,
    roundScores,
    showRoundEndOverlay,
    scoreToWin,
  } = useGameStore();

  const { nextRound } = useGameRoom(roomId ?? undefined, selectedGame ?? undefined);

  const handlePlayGame = async (gameType: GameType, mode: GameMode) => {
    // For dominoes games, show score limit picker first
    if (gameType.startsWith('dominoes')) {
      setPendingGame({ gameType, mode });
      setScoreLimit(150);
      return;
    }

    setSelectedGame(gameType);
    setGameMode(mode);

    try {
      await joinRoom(gameType, { vsAI: mode === 'ai' });
    } catch (err) {
      console.error('Failed to join game:', err);
      setSelectedGame(null);
      setGameMode(null);
    }
  };

  const handleStartWithScoreLimit = async () => {
    if (!pendingGame) return;
    setSelectedGame(pendingGame.gameType);
    setGameMode(pendingGame.mode);
    try {
      await joinRoom(pendingGame.gameType, { vsAI: pendingGame.mode === 'ai', scoreLimit });
    } catch (err) {
      console.error('Failed to join game:', err);
      setSelectedGame(null);
      setGameMode(null);
    }
    setPendingGame(null);
  };

  const handleLeaveGame = () => {
    leaveRoom();
    setSelectedGame(null);
    setGameMode(null);
    setSelectedDomino(null);
  };

  // Get valid arms for the selected domino
  const validArmsForSelected = useMemo(() => {
    if (!selectedDomino) return [];
    const play = validPlays.find(p => p.dominoId === selectedDomino);
    return play?.arms || [];
  }, [selectedDomino, validPlays]);

  // Get valid arms for the dragging domino
  const draggingValidArms = useMemo(() => {
    if (!draggingDomino) return [];
    const play = validPlays.find(p => p.dominoId === draggingDomino);
    return play?.arms || [];
  }, [draggingDomino, validPlays]);

  // Get list of draggable dominoes (ones that can be played)
  const draggableDominoes = useMemo(() => {
    if (currentTurnPlayerId !== mySessionId) return [];
    return validPlays.map(p => p.dominoId);
  }, [currentTurnPlayerId, mySessionId, validPlays]);

  const handleDragStart = (dominoId: string, e: React.DragEvent) => {
    setDraggingDomino(dominoId);
    setSelectedDomino(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dominoId);
  };

  const handleDragEnd = () => {
    setDraggingDomino(null);
  };

  const handleDrop = (arm: SpinnerArm) => {
    if (draggingDomino) {
      console.log('Dropping domino:', draggingDomino, 'on arm:', arm);
      invoke('PlaceDomino', draggingDomino, arm);
      setDraggingDomino(null);
    }
  };

  const handleDominoClick = (dominoId: string) => {
    if (currentTurnPlayerId !== mySessionId) {
      console.log('Not your turn!');
      return;
    }

    // Find valid plays for this domino
    const play = validPlays.find(p => p.dominoId === dominoId);

    if (!play || play.arms.length === 0) {
      console.log('This domino cannot be played');
      return;
    }

    // If only one arm is valid, play immediately
    if (play.arms.length === 1) {
      console.log('Playing domino:', dominoId, 'on arm:', play.arms[0]);
      invoke('PlaceDomino', dominoId, play.arms[0]);
      setSelectedDomino(null);
    } else {
      // Multiple arms available - select the domino and wait for arm selection
      setSelectedDomino(dominoId);
      console.log('Selected domino:', dominoId, 'Valid arms:', play.arms);
    }
  };

  const handleArmClick = (arm: SpinnerArm) => {
    if (!selectedDomino) return;

    console.log('Playing domino:', selectedDomino, 'on arm:', arm);
    invoke('PlaceDomino', selectedDomino, arm);
    setSelectedDomino(null);
  };

  const handlePlayCard = (cardId: string) => {
    if (currentTurnPlayerId === mySessionId) {
      invoke('PlayCard', cardId);
    }
  };

  const handleDrawFromBoneyard = () => {
    if (currentTurnPlayerId !== mySessionId) return;
    invoke('DrawFromBoneyard');
  };

  const handlePass = () => {
    if (currentTurnPlayerId !== mySessionId) return;
    invoke('PassTurn');
  };

  const handleBidSubmit = (bid: number) => {
    if (currentTurnPlayerId !== mySessionId) return;
    invoke('SubmitBid', bid);
  };

  const playerList = Array.from(players.values());
  const myPlayer = players.get(mySessionId || '');
  const isDominoes = selectedGame?.startsWith('dominoes');
  const isSpades = selectedGame === 'spades';
  const isMyTurn = currentTurnPlayerId === mySessionId;

  // Debug
  console.log('LobbyPage render:', { roomId, selectedGame, phase, playerCount: playerList.length, mySessionId, myHand: myPlayer?.hand });

  // Show game view if connected to a room
  if (roomId && selectedGame) {
    return (
      <div className="min-h-screen py-4 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold capitalize">{selectedGame.replace(/_/g, ' ')}</h1>
              <p className="text-gray-400">Room: {roomId}</p>
              <p className="text-sm text-gray-500">Phase: {phase} | First to {isSpades ? '500' : scoreToWin} wins</p>
            </div>
            <button
              onClick={handleLeaveGame}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
            >
              Leave Game
            </button>
          </div>

          {/* Scores - Dominoes style */}
          {phase !== 'waiting' && isDominoes && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              {playerList
                .sort((a, b) => a.seatIndex - b.seatIndex)
                .map(player => (
                  <div
                    key={player.sessionId}
                    className={`text-center p-3 rounded-lg ${
                      player.sessionId === mySessionId
                        ? 'bg-blue-600'
                        : currentTurnPlayerId === player.sessionId
                          ? 'bg-yellow-600'
                          : 'bg-shark-700'
                    }`}
                  >
                    <p className="text-sm font-medium truncate">{player.displayName}</p>
                    <p className="text-2xl font-bold">{player.score}</p>
                    {currentTurnPlayerId === player.sessionId && (
                      <p className="text-xs text-yellow-200">Playing</p>
                    )}
                  </div>
                ))}
            </div>
          )}

          {/* Scores - Spades style (team-based) */}
          {phase !== 'waiting' && isSpades && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className={`text-center p-4 rounded-lg ${myPlayer?.team === 'team1' ? 'bg-blue-600 ring-2 ring-blue-400' : 'bg-shark-700'}`}>
                <p className="text-sm font-medium">Team 1 {myPlayer?.team === 'team1' && '(You)'}</p>
                <p className="text-3xl font-bold">{team1Score}</p>
                <p className="text-sm text-gray-300">{team1Bags} bags | Bid: {team1Bid} | Won: {team1Tricks}</p>
              </div>
              <div className={`text-center p-4 rounded-lg ${myPlayer?.team === 'team2' ? 'bg-blue-600 ring-2 ring-blue-400' : 'bg-shark-700'}`}>
                <p className="text-sm font-medium">Team 2 {myPlayer?.team === 'team2' && '(You)'}</p>
                <p className="text-3xl font-bold">{team2Score}</p>
                <p className="text-sm text-gray-300">{team2Bags} bags | Bid: {team2Bid} | Won: {team2Tricks}</p>
              </div>
            </div>
          )}

          {/* Game Area */}
          <div className="glass-card-dark p-8 min-h-[400px]">
            {phase === 'finished' ? (
              <div className="text-center py-8">
                <h2 className="text-4xl font-bold mb-6 text-yellow-400">Game Over!</h2>
                {(() => {
                  const winner = playerList.reduce((prev, curr) =>
                    (prev?.score ?? 0) > (curr?.score ?? 0) ? prev : curr
                  , playerList[0]);
                  const isWinner = winner?.sessionId === mySessionId;
                  return (
                    <>
                      <p className="text-2xl mb-4">
                        {isWinner ? (
                          <span className="text-green-400">You Win!</span>
                        ) : (
                          <span className="text-red-400">{winner?.displayName} Wins!</span>
                        )}
                      </p>
                      <p className="text-xl text-gray-300 mb-6">Final Score: {winner?.score} points</p>
                      {/* Final scores table */}
                      <div className="max-w-md mx-auto mb-8">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-b border-shark-600">
                              <th className="py-2 px-3 text-gray-400 text-sm">Player</th>
                              <th className="py-2 px-3 text-gray-400 text-sm text-right">Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {playerList
                              .sort((a, b) => b.score - a.score)
                              .map((player) => (
                                <tr
                                  key={player.sessionId}
                                  className={`border-b border-shark-700 ${
                                    player.sessionId === winner?.sessionId ? 'bg-yellow-600/20' : ''
                                  } ${player.sessionId === mySessionId ? 'font-bold' : ''}`}
                                >
                                  <td className="py-2 px-3">
                                    {player.displayName}
                                    {player.sessionId === winner?.sessionId && ' \u{1F3C6}'}
                                  </td>
                                  <td className="py-2 px-3 text-right">{player.score}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex gap-4 justify-center">
                        <button
                          onClick={async () => {
                            const gt = selectedGame;
                            const gm = gameMode;
                            await leaveRoom();
                            if (gt && gm) {
                              handlePlayGame(gt, gm);
                            }
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-lg"
                        >
                          Next Game
                        </button>
                        <button
                          onClick={handleLeaveGame}
                          className="bg-shark-500 hover:bg-shark-600 text-white px-6 py-3 rounded-lg text-lg"
                        >
                          Quit
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : phase === 'waiting' ? (
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-6">Waiting for Players</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {[0, 1, 2, 3].map((seatIndex) => {
                    const player = playerList.find((p) => p.seatIndex === seatIndex);
                    return (
                      <div
                        key={seatIndex}
                        className={`p-4 rounded-lg ${
                          player ? 'bg-shark-700' : 'bg-shark-900 border-2 border-dashed border-shark-600'
                        }`}
                      >
                        {player ? (
                          <>
                            <p className="font-semibold">{player.displayName}</p>
                            <p className="text-sm text-gray-400">
                              {player.isReady ? '✓ Ready' : 'Not Ready'}
                            </p>
                          </>
                        ) : (
                          <p className="text-gray-500">Waiting...</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div>
                {/* Dominoes turn indicator */}
                {isDominoes && (
                  <div className="text-center mb-4">
                    <p className={`text-xl ${isMyTurn ? 'text-green-400 font-bold' : 'text-gray-400'}`}>
                      {isMyTurn
                        ? draggingDomino
                          ? 'Drop on a highlighted arm!'
                          : selectedDomino
                            ? 'Click an arm on the board to place your domino'
                            : "It's your turn! Drag a tile to play."
                        : 'Waiting for other player...'}
                    </p>
                  </div>
                )}

                {/* Game Board - Domino Board with Spinner Support */}
                {isDominoes && (
                  <DominoBoard
                    hasSpinner={hasSpinner}
                    spinnerId={spinnerId}
                    spinnerValue={spinnerValue}
                    leftArm={leftArm}
                    rightArm={rightArm}
                    topArm={topArm}
                    bottomArm={bottomArm}
                    preSpinnerTiles={preSpinnerTiles}
                    openEnds={openEnds}
                    boneyardCount={boneyardCount}
                    isMyTurn={isMyTurn}
                    selectedDomino={selectedDomino}
                    validArms={validArmsForSelected}
                    onArmClick={handleArmClick}
                    draggingDomino={draggingDomino}
                    draggingValidArms={draggingValidArms}
                    onDrop={handleDrop}
                  />
                )}

                {/* Game Board - Spades Table */}
                {isSpades && (
                  <SpadesTable
                    players={playerList}
                    mySessionId={mySessionId}
                    currentTurnPlayerId={currentTurnPlayerId}
                    phase={phase}
                    currentTrick={currentTrick}
                    bids={bids}
                    tricksWon={tricksWon}
                    team1Score={team1Score}
                    team1Bags={team1Bags}
                    team2Score={team2Score}
                    team2Bags={team2Bags}
                    team1Bid={team1Bid}
                    team2Bid={team2Bid}
                    team1Tricks={team1Tricks}
                    team2Tricks={team2Tricks}
                    roundNumber={roundNumber}
                    spadesBroken={spadesBroken}
                    onBidSubmit={handleBidSubmit}
                  />
                )}
              </div>
            )}
          </div>

          {/* My Hand */}
          {phase !== 'waiting' && phase !== 'finished' && myPlayer && (
            <div className={`mt-6 rounded-xl p-6 ${isMyTurn && phase === 'playing' ? 'bg-green-900/50 ring-2 ring-green-500' : 'bg-shark-800'}`}>
              <h3 className="text-lg font-semibold mb-4">
                Your Hand ({myPlayer.hand.length} {isDominoes ? 'tiles' : 'cards'})
                {phase === 'bidding' ? (
                  <span className="text-yellow-400 ml-2">- Bidding phase</span>
                ) : isMyTurn ? (
                  <span className="text-green-400 ml-2 animate-pulse">
                    {isDominoes
                      ? draggingDomino
                        ? '- Drop on a green zone!'
                        : selectedDomino
                          ? '- Select an arm on the board'
                          : '- YOUR TURN! Drag a tile to an arm'
                      : '- YOUR TURN! Click a card to play'}
                  </span>
                ) : (
                  <span className="text-gray-400 ml-2">- Waiting...</span>
                )}
              </h3>
              {myPlayer.hand.length > 0 ? (
                isDominoes ? (
                  <div className={!isMyTurn ? 'opacity-50 pointer-events-none' : ''}>
                    <DominoHand
                      dominoes={myPlayer.hand}
                      selectedDominoes={selectedDomino ? [selectedDomino] : []}
                      onDominoClick={handleDominoClick}
                      size="lg"
                      draggableDominoes={draggableDominoes}
                      onDominoDragStart={handleDragStart}
                      onDominoDragEnd={handleDragEnd}
                    />
                    {/* Show which dominoes are playable */}
                    {isMyTurn && validPlays.length > 0 && (
                      <p className="text-sm text-gray-400 mt-2">
                        Drag a playable tile to an arm: {validPlays.map(p => p.dominoId).join(', ')}
                      </p>
                    )}
                  </div>
                ) : isSpades ? (
                  <div className={phase !== 'playing' || !isMyTurn ? 'opacity-50 pointer-events-none' : ''}>
                    <CardHand
                      cards={myPlayer.hand}
                      selectedCards={validCards}
                      onCardClick={(cardId) => {
                        if (isMyTurn && phase === 'playing' && validCards.includes(cardId)) {
                          handlePlayCard(cardId);
                        }
                      }}
                      size="lg"
                    />
                    {isMyTurn && phase === 'playing' && validCards.length > 0 && (
                      <p className="text-sm text-gray-400 mt-2">
                        Valid plays: {validCards.length} cards {!spadesBroken && '(Spades not broken)'}
                      </p>
                    )}
                  </div>
                ) : (
                  <CardHand
                    cards={myPlayer.hand}
                    onCardClick={handlePlayCard}
                    size="lg"
                  />
                )
              ) : (
                <p className="text-gray-400">No {isDominoes ? 'tiles' : 'cards'} in hand</p>
              )}
              {/* Draw/Pass buttons for dominoes */}
              {isDominoes && isMyTurn && phase === 'playing' && (
                <div className="mt-4 flex gap-4 justify-center">
                  {selectedDomino && (
                    <button
                      onClick={() => setSelectedDomino(null)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
                    >
                      Cancel Selection
                    </button>
                  )}
                  {boneyardCount > 0 ? (
                    <button
                      onClick={handleDrawFromBoneyard}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
                    >
                      Draw from Boneyard ({boneyardCount} left)
                    </button>
                  ) : (
                    <button
                      onClick={handlePass}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg"
                    >
                      Pass (No tiles to draw)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Round-end overlay */}
          {showRoundEndOverlay && (() => {
            const sortedPlayers = Array.from(players.values()).sort((a, b) => a.seatIndex - b.seatIndex);
            const scoreLeader = sortedPlayers.reduce((prev, curr) =>
              (curr.score > prev.score) ? curr : prev, sortedPlayers[0]);
            const wentOutPlayer = roundResult?.winnerId ? players.get(roundResult.winnerId) : null;
            return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-shark-900/95">
              <div className="bg-shark-800 rounded-xl p-8 max-w-md w-full mx-4 text-center">
                <h2 className="text-4xl font-bold mb-2 text-yellow-400">
                  {isDominoes
                    ? (roundResult?.isBlocked ? 'Blocked!' : 'Domino!')
                    : `Round ${roundNumber} Complete`}
                </h2>
                {isDominoes && wentOutPlayer && !roundResult?.isBlocked && (
                  <p className="text-lg text-gray-300 mb-2">
                    <span className="text-white font-semibold">{wentOutPlayer.displayName}</span> went out!
                  </p>
                )}
                {scoreLeader && (
                  <p className="text-xl text-green-400 font-semibold mb-6">
                    {scoreLeader.sessionId === mySessionId ? 'You are' : `${scoreLeader.displayName} is`} in the lead with {scoreLeader.score} pts
                  </p>
                )}
                {/* Score table */}
                <table className="w-full text-left mb-6">
                  <thead>
                    <tr className="border-b border-shark-600">
                      <th className="py-2 px-3 text-gray-400 text-sm">Player</th>
                      <th className="py-2 px-3 text-gray-400 text-sm text-right">Round</th>
                      <th className="py-2 px-3 text-gray-400 text-sm text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPlayers.map((player) => (
                        <tr
                          key={player.sessionId}
                          className={`border-b border-shark-700 ${
                            player.sessionId === scoreLeader?.sessionId ? 'bg-green-600/20' : ''
                          }`}
                        >
                          <td className="py-2 px-3">
                            {player.displayName}
                            {player.sessionId === mySessionId && ' (You)'}
                            {isDominoes && player.sessionId === roundResult?.winnerId && !roundResult?.isBlocked && ' 🁣'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            +{roundScores?.[player.sessionId]?.score ?? 0}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold">
                            {player.score}/{isSpades ? 500 : scoreToWin}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <button
                  onClick={() => {
                    nextRound();
                    useGameStore.getState().setRoomState({
                      showRoundEndOverlay: false,
                      roundResult: null,
                      roundScores: null,
                    });
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-lg"
                >
                  Next Round
                </button>
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Show lobby
  return (
    <div className="min-h-screen py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col items-center mb-10">
          <img src="/cardsharkie-logo.png" alt="CardSharkie Games" className="w-28 h-28 mb-3 rounded-full ring-2 ring-shark-400/30 shadow-[0_0_30px_rgba(14,165,233,0.2)]" />
          <h1 className="brand-text text-4xl mb-4">CardSharkie</h1>
          <p className="text-gray-400 text-lg">Choose a Game</p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6 text-center">
            {error}
          </div>
        )}

        {isConnecting ? (
          <div className="glass-card-dark p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shark-400 mx-auto mb-4"></div>
            <p className="text-xl mb-4">
              {gameMode === 'ai' ? 'Starting game' : 'Finding players'}...
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {games.map((game) => (
              <div key={game.id} className="glass-card-dark p-6">
                <h3 className="text-2xl font-semibold mb-2">{game.name}</h3>
                <p className="text-gray-400 mb-4">{game.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-shark-400">{game.players}</span>
                  <div className="flex gap-2">
                    {game.supportsAI && (
                      <button
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                        disabled={isConnecting}
                        onClick={() => handlePlayGame(game.id, 'ai')}
                      >
                        vs AI
                      </button>
                    )}
                    <button
                      className="bg-shark-500 hover:bg-shark-600 text-white px-4 py-2 rounded-lg"
                      disabled={isConnecting}
                      onClick={() => handlePlayGame(game.id, 'quick')}
                    >
                      Quick Match
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Score limit picker modal for dominoes */}
        {pendingGame && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-shark-900/95">
            <div className="bg-shark-800 rounded-xl p-8 max-w-md w-full mx-4 text-center">
              <h2 className="text-2xl font-bold mb-6">Select Point Target</h2>
              <div className="flex gap-4 justify-center mb-8">
                {[150, 300, 500].map((value) => (
                  <button
                    key={value}
                    onClick={() => setScoreLimit(value)}
                    className={`px-6 py-3 rounded-lg text-lg font-semibold transition-colors ${
                      scoreLimit === value
                        ? 'bg-yellow-600 text-white ring-2 ring-yellow-400'
                        : 'bg-shark-700 hover:bg-shark-600 text-gray-300'
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleStartWithScoreLimit}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg text-lg"
                >
                  Start Game
                </button>
                <button
                  onClick={() => setPendingGame(null)}
                  className="bg-shark-500 hover:bg-shark-600 text-white px-6 py-3 rounded-lg text-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
